import { getLayout } from "../lib/theme";

// Live scoped mini-preview reflecting each skin's palette AND structure.
export default function SkinPreview({ skinId }) {
  const L = getLayout(skinId);
  const line = (w) => <span className={`block h-1 ${w} rounded-full bg-[var(--text-secondary)] opacity-60`} />;

  const NavBar = (
    <div className={`mb-1.5 flex items-center justify-between ${L.nav === "utility" ? "border-b-2 border-[var(--accent)] pb-1" : L.nav === "guide" ? "border-t-2 border-[var(--accent)] pt-1" : L.nav === "scanline" ? "border-b-2 border-[var(--border-accent)] pb-1" : ""} ${L.nav === "float" ? "justify-center" : ""}`}>
      <span className={`font-head text-[9px] leading-none tracking-widest ${L.nav === "utility" ? "rounded-sm bg-[var(--accent)] px-1" : ""} ${L.nav === "scanline" ? "neon-text" : ""}`}>
        <span className={L.nav === "utility" ? "text-[var(--accent-contrast)]" : "text-[var(--accent)]"}>THE&nbsp;</span>
        <span className={L.nav === "utility" ? "text-[var(--accent-contrast)]" : "text-[var(--text-primary)]"}>DIAL</span>
      </span>
      {L.nav !== "float" && <span className="h-1 w-4 rounded-full bg-[var(--accent)]" />}
    </div>
  );

  let Body;
  if (L.home === "social") {
    Body = (
      <div className="flex gap-1">
        <div className="w-1/3 space-y-0.5">
          <div className="h-1.5 rounded-sm bg-[var(--accent)]" />
          <div className="h-6 rounded-sm border border-dotted border-[var(--border-subtle)] bg-[var(--bg-card)]" />
        </div>
        <div className="flex-1 space-y-1">
          <div className="h-1 rounded-sm" style={{ backgroundImage: "repeating-linear-gradient(90deg,#ff46b0 0 3px,#27e0e0 3px 6px)" }} />
          <div className="grid grid-cols-4 gap-0.5">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-3 rounded-sm border border-dotted border-[var(--border-subtle)] bg-[var(--bg-card)]" />)}
          </div>
          <div className="h-1 rounded-sm" style={{ backgroundImage: "repeating-linear-gradient(90deg,#ff46b0 0 3px,#27e0e0 3px 6px)" }} />
        </div>
      </div>
    );
  } else if (L.home === "guide") {
    Body = (
      <div className="grid grid-cols-4 gap-px bg-[var(--border-subtle)]">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className={`h-3 ${i === 5 ? "bg-[var(--accent)]" : "bg-[var(--bg-card)]"}`} />
        ))}
      </div>
    );
  } else {
    const Hero = L.hero === "masthead" ? (
      <div className="mb-1.5 flex gap-1"><div className="h-9 w-1/2 rounded-sm border border-[var(--border-subtle)] bg-[var(--bg-card)]" /><div className="flex w-1/2 flex-col justify-center gap-1">{line("w-10")}{line("w-7")}<span className="block h-2 w-6 rounded-sm bg-[var(--accent)]" /></div></div>
    ) : L.hero === "spotlight" ? (
      <div className="mb-1.5 flex items-center justify-center gap-1.5 rounded-sm bg-[var(--bg-card)] py-1.5"><span className="block h-8 w-5 rounded-sm border border-[var(--border-subtle)] bg-[var(--bg-base)]" /><div className="flex flex-col gap-1">{line("w-9")}<span className="block h-2 w-5 rounded-sm bg-[var(--accent)]" /></div></div>
    ) : L.hero === "wall" ? (
      <div className="mb-1.5 flex gap-1 rounded-sm bg-[var(--bg-card)] p-1">{[0,1,2].map(i=><span key={i} className="block h-8 flex-1 rounded-sm border border-[var(--border-subtle)] bg-[var(--bg-base)]" />)}</div>
    ) : L.hero === "marquee" ? (
      <div className="relative mb-1.5 h-10 overflow-hidden rounded-sm bg-[var(--bg-card)]"><div className="absolute inset-x-0 bottom-0 h-4" style={{ backgroundImage: "linear-gradient(var(--border-accent) 1px,transparent 1px),linear-gradient(90deg,var(--border-accent) 1px,transparent 1px)", backgroundSize: "6px 6px" }} /><div className="absolute inset-0 flex items-center justify-center"><span className="font-head text-[8px] tracking-widest text-[var(--accent)]" style={{ textShadow: "0 0 6px var(--accent-glow)" }}>NEON</span></div></div>
    ) : (
      <div className={`relative mb-1.5 overflow-hidden rounded-sm bg-[var(--bg-card)] ${L.hero === "colossal" ? "h-12" : "h-10"}`}><div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-base)] to-transparent" /><div className="absolute bottom-1 left-1 flex flex-col gap-0.5">{line("w-10")}<span className="block h-2 w-5 rounded-sm bg-[var(--accent)]" /></div></div>
    );

    const Cards = (
      <div className="flex gap-1">
        {[0, 1, 2].map((i) =>
          L.card === "tile" ? <div key={i} className="flex-1"><div className="h-5 rounded-sm border border-[var(--border-subtle)] bg-[var(--bg-card)]" />{line("w-8")}</div>
          : L.card === "box" ? <div key={i} className="flex-1"><div className="h-7 rounded-sm border-2 border-[var(--border-subtle)] bg-[var(--bg-card)]" /><span className="block h-0.5 w-full bg-[var(--accent)] opacity-40" />{line("w-6")}</div>
          : L.card === "bigtile" ? <div key={i} className="relative h-6 flex-1 overflow-hidden rounded-sm bg-[var(--bg-card)]"><div className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-t from-black/80 to-transparent" /></div>
          : L.card === "shelf" ? <div key={i} className="flex-1"><div className="reflect h-7 rounded-sm border border-[var(--border-subtle)] bg-[var(--bg-card)]" /></div>
          : L.card === "cell" ? <div key={i} className="h-6 flex-1 border border-[var(--border-subtle)] bg-[var(--bg-card)]" />
          : L.card === "cassette" ? <div key={i} className="flex-1"><div className="border border-[var(--border-accent)] bg-[var(--bg-card)]"><div className="h-1 border-b border-[var(--border-accent)] bg-[var(--bg-surface)]" /><div className="h-6" /></div><span className="mt-0.5 block h-0.5 w-6 bg-[var(--accent)] opacity-50" /></div>
          : <div key={i} className="relative h-9 flex-1 overflow-hidden rounded-sm border border-[var(--border-subtle)] bg-[var(--bg-card)]"><div className="absolute inset-x-0 bottom-0 h-3 bg-gradient-to-t from-black/80 to-transparent" /></div>
        )}
      </div>
    );
    Body = <>{Hero}{Cards}</>;
  }

  return (
    <div data-skin={skinId} className="overflow-hidden rounded-sm border border-[var(--border-subtle)]" data-testid={`skin-preview-${skinId}`}>
      <div className="bg-[var(--bg-base)] p-2">{NavBar}{Body}</div>
    </div>
  );
}
